/**
 * Sound, synthesised with the Web Audio API (no audio files).
 *
 *  - A soft pad per chapter that brightens as more telescopes light up.
 *  - Bell-like pentatonic chimes: each telescope owns one note of the level's chord;
 *    completing a level plays the whole chord.
 *  - A low "whoomp" when a beam falls into a black hole, and quiet ticks for pick-up and drop.
 *
 * Nothing is created until the first user gesture (browsers require it), and every call is a
 * no-op while sound is off or unavailable, so the game works the same muted.
 */

type Mode = 'major' | 'minor';

const CHORDS: Record<Mode, number[]> = {
  // Semitones above the root for successive telescopes (pentatonic chord tones).
  major: [0, 4, 7, 12, 16, 19, 24, 28],
  minor: [0, 3, 7, 12, 15, 19, 24, 27],
};

const midiToHz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

export interface Key {
  root: number; // MIDI note of the chime root
  mode: Mode;
}

export const CHAPTER_KEYS: Record<number, Key> = {
  1: { root: 72, mode: 'major' }, // C5
  2: { root: 74, mode: 'major' }, // D5
  3: { root: 69, mode: 'minor' }, // A4 minor
};

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private padBus: GainNode | null = null;
  private padFilter: BiquadFilterNode | null = null;
  private padVoices: OscillatorNode[] = [];
  private padLfo: OscillatorNode | null = null;
  private noise: AudioBuffer | null = null;
  private enabled = true;
  private volume = 0.7;
  private key: Key = CHAPTER_KEYS[1]!;
  private lastWhoomp = 0;
  private lastChime = new Map<number, number>();
  private wantPad = false;
  unavailable = false;

  configure(enabled: boolean, volume: number) {
    this.enabled = enabled;
    this.volume = volume;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(enabled ? volume * 0.9 : 0, this.ctx.currentTime, 0.05);
    }
    if (enabled && this.wantPad) this.startPad();
    if (!enabled) this.stopPad(true);
  }

  /** Call from a user gesture (pointerdown / keydown). Creates or resumes the context. */
  unlock(): void {
    if (!this.enabled || this.unavailable) return;
    try {
      if (!this.ctx) {
        const Ctor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) {
          this.unavailable = true;
          return;
        }
        this.ctx = new Ctor();
        const comp = this.ctx.createDynamicsCompressor();
        comp.threshold.value = -18;
        comp.ratio.value = 3;
        this.master = this.ctx.createGain();
        this.master.gain.value = this.volume * 0.9;
        this.master.connect(comp);
        comp.connect(this.ctx.destination);
        // One second of white noise, reused for transients.
        const len = this.ctx.sampleRate;
        this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
        const d = this.noise.getChannelData(0);
        let seed = 12345;
        for (let i = 0; i < len; i++) {
          seed = (seed * 1103515245 + 12345) & 0x7fffffff;
          d[i] = (seed / 0x7fffffff) * 2 - 1;
        }
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      if (this.wantPad) this.startPad();
    } catch {
      this.unavailable = true;
    }
  }

  /** Pause everything while the tab is hidden. */
  setHidden(hidden: boolean) {
    if (!this.ctx) return;
    if (hidden) void this.ctx.suspend();
    else if (this.enabled) void this.ctx.resume();
  }

  private live(): AudioContext | null {
    return this.enabled && this.ctx && this.master && this.ctx.state === 'running'
      ? this.ctx
      : null;
  }

  setKey(key: Key) {
    const changed = key.root !== this.key.root || key.mode !== this.key.mode;
    this.key = key;
    if (changed && this.padVoices.length) {
      this.stopPad();
      this.startPad();
    }
  }

  noteFor(index: number): number {
    const tones = CHORDS[this.key.mode];
    return midiToHz(this.key.root + tones[index % tones.length]!);
  }

  // ---------------------------------------------------------------------------- pad

  requestPad(on: boolean) {
    this.wantPad = on;
    if (on) this.startPad();
    else this.stopPad();
  }

  private startPad() {
    const ctx = this.live();
    if (!ctx || !this.master || this.padVoices.length) return;
    const bus = ctx.createGain();
    bus.gain.value = 0;
    bus.gain.setTargetAtTime(0.055, ctx.currentTime, 1.2);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 420;
    filter.Q.value = 0.6;
    filter.connect(bus);
    bus.connect(this.master);
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.07;
    lfoGain.gain.value = 90;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start();
    const root = this.key.root - 24;
    const tones = this.key.mode === 'major' ? [0, 7, 14, 16] : [0, 7, 14, 15];
    for (const t of tones) {
      for (const det of [-6, 5]) {
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = midiToHz(root + t);
        o.detune.value = det;
        o.connect(filter);
        o.start();
        this.padVoices.push(o);
      }
    }
    this.padBus = bus;
    this.padFilter = filter;
    this.padLfo = lfo;
  }

  private stopPad(immediate = false) {
    const ctx = this.ctx;
    if (!ctx || !this.padBus) return;
    const bus = this.padBus;
    const voices = this.padVoices;
    const lfo = this.padLfo;
    const t = ctx.currentTime;
    bus.gain.cancelScheduledValues(t);
    bus.gain.setTargetAtTime(0, t, immediate ? 0.02 : 0.6);
    const stopAt = t + (immediate ? 0.2 : 3);
    voices.forEach((o) => o.stop(stopAt));
    lfo?.stop(stopAt);
    this.padVoices = [];
    this.padBus = null;
    this.padFilter = null;
    this.padLfo = null;
  }

  /** 0..1: how much of the level is lit; opens the pad's filter. */
  padBrightness(f: number) {
    const ctx = this.live();
    if (!ctx || !this.padFilter) return;
    this.padFilter.frequency.setTargetAtTime(
      420 + 1500 * Math.max(0, Math.min(1, f)),
      ctx.currentTime,
      0.4,
    );
  }

  // ---------------------------------------------------------------------------- voices

  private bell(freq: number, when: number, gain: number, length = 2.2) {
    const ctx = this.live();
    if (!ctx || !this.master) return;
    // Partials of a struck bell (approximate inharmonic ratios).
    const partials: [number, number, number][] = [
      [1, 1, 1],
      [2.0, 0.28, 0.7],
      [2.76, 0.22, 0.5],
      [5.4, 0.08, 0.3],
    ];
    for (const [ratio, amp, decay] of partials) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq * ratio;
      g.gain.setValueAtTime(0, when);
      g.gain.linearRampToValueAtTime(gain * amp, when + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, when + length * decay);
      o.connect(g);
      g.connect(this.master);
      o.start(when);
      o.stop(when + length * decay + 0.05);
    }
  }

  /** A telescope lit up. */
  chime(index: number) {
    const ctx = this.live();
    if (!ctx) return;
    const now = performance.now();
    if (now - (this.lastChime.get(index) ?? 0) < 140) return;
    this.lastChime.set(index, now);
    this.bell(this.noteFor(index), ctx.currentTime + 0.01, 0.16);
  }

  /** A telescope went dark: a soft low tick, so the change is audible but not punishing. */
  dim() {
    const ctx = this.live();
    if (!ctx || !this.master) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(this.noteFor(0) / 2, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(this.noteFor(0) / 3, ctx.currentTime + 0.15);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.05, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
    o.connect(g);
    g.connect(this.master);
    o.start();
    o.stop(ctx.currentTime + 0.25);
  }

  /** Level complete: the whole chord, rolled upwards. */
  chord(count: number) {
    const ctx = this.live();
    if (!ctx) return;
    const n = Math.max(3, count + 1);
    for (let i = 0; i < n; i++) {
      this.bell(this.noteFor(i), ctx.currentTime + 0.05 + i * 0.09, 0.13, 3.2);
    }
    this.bell(this.noteFor(0) / 2, ctx.currentTime + 0.05, 0.1, 4);
  }

  /** A beam fell into a black hole. */
  whoomp() {
    const ctx = this.live();
    if (!ctx || !this.master || !this.noise) return;
    const now = performance.now();
    if (now - this.lastWhoomp < 450) return;
    this.lastWhoomp = now;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(150, t);
    o.frequency.exponentialRampToValueAtTime(36, t + 0.55);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.32, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + 0.75);
    const n = ctx.createBufferSource();
    n.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(600, t);
    f.frequency.exponentialRampToValueAtTime(80, t + 0.5);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.12, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    n.connect(f);
    f.connect(ng);
    ng.connect(this.master);
    n.start(t);
    n.stop(t + 0.55);
  }

  /** Quiet tactile tick: 'lift' when picking up, 'drop' when placing, 'nope' for refusals. */
  tick(kind: 'lift' | 'drop' | 'nope' | 'ui') {
    const ctx = this.live();
    if (!ctx || !this.master || !this.noise) return;
    const t = ctx.currentTime;
    const n = ctx.createBufferSource();
    n.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value =
      kind === 'lift' ? 2600 : kind === 'drop' ? 1400 : kind === 'nope' ? 500 : 3200;
    f.Q.value = 2.5;
    const g = ctx.createGain();
    const peak = kind === 'ui' ? 0.05 : 0.11;
    g.gain.setValueAtTime(peak, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + (kind === 'nope' ? 0.12 : 0.05));
    n.connect(f);
    f.connect(g);
    g.connect(this.master);
    n.start(t);
    n.stop(t + 0.15);
    if (kind === 'drop' || kind === 'nope') {
      const o = ctx.createOscillator();
      const og = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = kind === 'drop' ? 520 : 180;
      og.gain.setValueAtTime(0.0001, t);
      og.gain.linearRampToValueAtTime(0.06, t + 0.005);
      og.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
      o.connect(og);
      og.connect(this.master);
      o.start(t);
      o.stop(t + 0.1);
    }
  }
}

export const audio = new AudioEngine();

/** Gentle haptics on phones that support it. */
export function buzz(pattern: number | number[], enabled: boolean): void {
  if (!enabled) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Some browsers throw when vibration is blocked; ignore.
  }
}
